import React, { useState } from 'react';
import { useAuth } from './AuthContext';

const C = {
  border:     'rgba(255,255,255,0.07)',
  borderGreen:'rgba(29,224,80,0.25)',
  green:      '#1de050',
  greenDim:   'rgba(29,224,80,0.11)',
  red:        '#ff4545',
  amber:      '#ffaa00',
  text:       '#cdd9d2',
  dim:        'rgba(155,182,162,0.55)',
  dimmer:     'rgba(115,148,125,0.38)',
};

const Spin = () => (
  <div style={{ width:16, height:16, border:'2px solid rgba(29,224,80,0.2)', borderTop:'2px solid #1de050', borderRadius:'50%', animation:'adm-spin 0.75s linear infinite', flexShrink:0 }}/>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

function parseFirebaseError(err) {
  const code = err.code || '';
  if (code.includes('wrong-password') || code.includes('invalid-credential')) return 'Incorrect email or password.';
  if (code.includes('user-not-found'))    return 'No account found with this email.';
  if (code.includes('email-already'))     return 'Account already exists. Sign in instead.';
  if (code.includes('weak-password'))     return 'Password must be at least 6 characters.';
  if (code.includes('invalid-email'))     return 'Please enter a valid email address.';
  if (code.includes('too-many-requests')) return 'Too many attempts. Wait a moment and try again.';
  if (code.includes('popup-closed'))      return '';
  return err.message || 'Something went wrong. Please try again.';
}

export default function AuthModal({ onClose, onSuccess, initialMode = 'signin' }) {
  const { signIn, signUp, signInWithGoogle, resetPassword, firebaseEnabled } = useAuth();
  const [mode,     setMode]     = useState(initialMode);
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState('');
  const [sent,     setSent]     = useState(false);

  const inp = {
    background:'rgba(10,16,14,0.8)', border:'1px solid rgba(35,70,45,0.5)',
    borderRadius:8, padding:'12px 14px', color:C.text, fontSize:13,
    fontFamily:'inherit', width:'100%', outline:'none', boxSizing:'border-box',
  };

  const handleSubmit = async () => {
    setErr(''); setSent(false);
    if (!email.trim()) { setErr('Email is required.'); return; }
    if (mode === 'reset') {
      setLoading(true);
      try { await resetPassword(email.trim()); setSent(true); }
      catch(e) { setErr(parseFirebaseError(e)); }
      setLoading(false); return;
    }
    if (!password) { setErr('Password is required.'); return; }
    if (mode === 'signup' && password !== confirm) { setErr('Passwords do not match.'); return; }
    if (mode === 'signup' && password.length < 6)  { setErr('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      if (mode === 'signin') await signIn(email.trim(), password);
      else await signUp(email.trim(), password);
      onSuccess && onSuccess(); onClose();
    } catch(e) {
      const msg = parseFirebaseError(e);
      if (msg) setErr(msg);
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setErr(''); setLoading(true);
    try {
      await signInWithGoogle();
      onSuccess && onSuccess(); onClose();
    } catch(e) {
      const msg = parseFirebaseError(e);
      if (msg) setErr(msg);
    }
    setLoading(false);
  };

  const titles = { signin:'Sign In to A.D.M', signup:'Create Account', reset:'Reset Password' };
  const subs   = { signin:'Access your intelligence dashboard', signup:'Start monitoring UAP activity', reset:'Enter your email to receive a reset link' };

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:'fixed',inset:0,zIndex:200,background:'rgba(3,6,9,0.92)',backdropFilter:'blur(16px)',display:'flex',alignItems:'center',justifyContent:'center',padding:'20px',paddingBottom:'max(20px,env(safe-area-inset-bottom))'}}>
      <div style={{background:'rgba(7,11,15,0.99)',border:`1px solid ${C.border}`,borderRadius:16,width:'100%',maxWidth:420,overflow:'hidden',boxShadow:'0 40px 100px rgba(0,0,0,0.75)',animation:'adm-up 0.2s ease-out'}}>
        <div style={{padding:'24px 24px 0'}}>

          {/* Header */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:22}}>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                <div style={{width:26,height:26,borderRadius:'50%',background:'rgba(8,14,10,0.92)',border:`1.5px solid ${C.borderGreen}`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg width="14" height="14" viewBox="0 0 22 24">
                    <ellipse cx="11" cy="11" rx="8.5" ry="10.5" fill="#1de050" opacity="0.93"/>
                    <ellipse cx="7.8" cy="8.5" rx="2.9" ry="2.1" fill="rgba(4,10,6,0.96)" transform="rotate(-20,7.8,8.5)"/>
                    <ellipse cx="14.2" cy="8.5" rx="2.9" ry="2.1" fill="rgba(4,10,6,0.96)" transform="rotate(20,14.2,8.5)"/>
                  </svg>
                </div>
                <span style={{fontWeight:800,fontSize:15,letterSpacing:2,color:'#e4ede7'}}>A.D.M</span>
              </div>
              <div style={{fontSize:19,fontWeight:700,color:C.text}}>{titles[mode]}</div>
              <div style={{fontSize:12,color:C.dim,marginTop:3}}>{subs[mode]}</div>
            </div>
            <button onClick={onClose} style={{background:'none',border:'none',color:C.dimmer,fontSize:22,cursor:'pointer',lineHeight:1,padding:'0 0 0 8px',WebkitTapHighlightColor:'transparent'}}>✕</button>
          </div>

          {/* Firebase not configured warning */}
          {!firebaseEnabled && (
            <div style={{background:'rgba(255,170,0,0.08)',border:'1px solid rgba(255,170,0,0.3)',borderRadius:8,padding:'10px 12px',marginBottom:16,fontSize:11,color:C.amber,lineHeight:1.6}}>
              ⚠ Firebase not configured. Add REACT_APP_FIREBASE_* keys to Netlify to enable accounts.
            </div>
          )}

          {/* Google Sign In — only shown when explicitly enabled */}
          {mode !== 'reset' && process.env.REACT_APP_GOOGLE_ENABLED === 'true' && (
            <button onClick={handleGoogle} disabled={loading||!firebaseEnabled} style={{width:'100%',padding:'11px',background:'rgba(255,255,255,0.06)',border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:9,marginBottom:14,transition:'all 0.15s',WebkitTapHighlightColor:'transparent',opacity:!firebaseEnabled?0.4:1}}>
              <GoogleIcon/> Continue with Google
            </button>
          )}

          {mode !== 'reset' && process.env.REACT_APP_GOOGLE_ENABLED === 'true' && (
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
              <div style={{flex:1,height:1,background:C.border}}/>
              <span style={{fontSize:11,color:C.dimmer}}>or with email</span>
              <div style={{flex:1,height:1,background:C.border}}/>
            </div>
          )}

          {/* Form fields */}
          <div style={{display:'flex',flexDirection:'column',gap:9,marginBottom:14}}>
            <input style={inp} type="email" placeholder="Email address" value={email}
              onChange={e=>setEmail(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleSubmit()}
              autoComplete="email"/>
            {mode !== 'reset' && (
              <input style={inp} type="password" placeholder="Password"
                value={password} onChange={e=>setPassword(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&handleSubmit()}
                autoComplete={mode==='signup'?'new-password':'current-password'}/>
            )}
            {mode === 'signup' && (
              <input style={inp} type="password" placeholder="Confirm password"
                value={confirm} onChange={e=>setConfirm(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&handleSubmit()}
                autoComplete="new-password"/>
            )}
          </div>

          {/* Error / success */}
          {err && <div style={{color:C.red,fontSize:12,marginBottom:12,padding:'9px 12px',background:'rgba(255,69,69,0.07)',border:'1px solid rgba(255,69,69,0.2)',borderRadius:7,lineHeight:1.5}}>{err}</div>}
          {sent && <div style={{color:C.green,fontSize:12,marginBottom:12,padding:'9px 12px',background:'rgba(29,224,80,0.07)',border:`1px solid ${C.borderGreen}`,borderRadius:7}}>Reset email sent! Check your inbox.</div>}

          {/* Submit */}
          <button onClick={handleSubmit} disabled={loading||!firebaseEnabled} style={{width:'100%',padding:'12px',background:C.greenDim,border:`1px solid ${C.borderGreen}`,borderRadius:8,color:C.green,fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:8,transition:'all 0.15s',WebkitTapHighlightColor:'transparent',opacity:!firebaseEnabled?0.4:1}}>
            {loading ? <><Spin/>Processing...</> :
             mode==='signin' ? 'Sign In' :
             mode==='signup' ? 'Create Account' :
             'Send Reset Email'}
          </button>

          {/* Mode switcher */}
          <div style={{padding:'16px 0 20px',display:'flex',flexDirection:'column',gap:8,alignItems:'center'}}>
            {mode === 'signin' && <>
              <span style={{fontSize:12,color:C.dimmer}}>
                No account?{' '}
                <span onClick={()=>{setMode('signup');setErr('');}} style={{color:C.green,cursor:'pointer',fontWeight:600}}>Create one free</span>
              </span>
              <span onClick={()=>{setMode('reset');setErr('');}} style={{fontSize:11,color:C.dimmer,cursor:'pointer',textDecoration:'underline'}}>Forgot password?</span>
            </>}
            {mode === 'signup' && (
              <span style={{fontSize:12,color:C.dimmer}}>
                Already have an account?{' '}
                <span onClick={()=>{setMode('signin');setErr('');}} style={{color:C.green,cursor:'pointer',fontWeight:600}}>Sign in</span>
              </span>
            )}
            {mode === 'reset' && (
              <span onClick={()=>{setMode('signin');setErr('');setSent(false);}} style={{fontSize:12,color:C.green,cursor:'pointer',fontWeight:600}}>← Back to sign in</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
